import {
	Button,
	Card,
	CardContent,
	Grid,
	Typography,
	Tooltip,
	FormHelperText,
	Stack,
	Divider,
	Box,
	List,
	ListItem,
	ListItemText,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningOutlinedIcon from '@mui/icons-material/WarningOutlined';
import CardTitleLogo from '../../assets/images/logos/OrangeArrows.svg';
import buildingIconBlack from '../../assets/images/icons/buildingIconBlack.svg';
import briefcaseIcon from '../../assets/images/icons/briefcaseIcon.svg';
import InfoIcon from '../../assets/images/icons/infoIcon.svg';
import {
	formatCurrency,
	MAX_FORWARD_DAYS,
	MAX_POLICY_PERIOD,
	MIN_POLICY_PERIOD,
} from '../../helper/constants';
import { QuestionText } from '../ProductQuestions/questionText';
import { declineMessages } from '../ProductQuestions/declineMessages';
import { useQuoteContext } from '../../shared/contexts/QuoteContext';
import { colours } from '../../shared/colors';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import ErrorMessage from '../ErrorMessage';
import { ReferralReasonDisplay } from '../../shared/types/quotes';

const ThickDivider = () => (
	<Divider
		sx={{
			mb: '1em',
			borderBottomWidth: '5px',
			borderColor: colours.primaryLight.main,
		}}
	/>
);

const ReferralMessage = () => {
	const { allReferralReasons } = useQuoteContext();

	const clientReasons = allReferralReasons.filter((r) => r.type === 'client');

	const packageReasons = allReferralReasons
		.filter((r) => r.type === 'package')
		.reduce<Record<string, ReferralReasonDisplay[]>>((acc, reason) => {
			const { packageId } = reason;
			if (packageId) {
				if (!acc[packageId]) {
					acc[packageId] = [];
				}
				acc[packageId].push(reason);
			}
			return acc;
		}, {});

	const productReasons = allReferralReasons
		.filter((r) => r.type === 'product')
		.reduce<Record<string, ReferralReasonDisplay[]>>((acc, reason) => {
			const { productId } = reason;
			if (productId) {
				if (!acc[productId]) {
					acc[productId] = [];
				}
				acc[productId].push(reason);
			}
			return acc;
		}, {});

	return (
		<Stack spacing={1} py={'1em'}>
			<Typography variant='cardSubtitle2'>{'Referral reason(s)'}</Typography>
			{clientReasons.length > 0 && (
				<Grid container rowSpacing={1}>
					<Grid item xs={4}>
						<Typography variant='cardSubtitle2'>{'Client'}</Typography>
					</Grid>
					<Grid item xs={8}>
						<Typography variant='cardSubtitle2'>
							{clientReasons.map((reason) => reason.name).join(', ')}
						</Typography>
					</Grid>
				</Grid>
			)}
			{Object.entries(packageReasons).map(([packageId, values]) => (
				<Grid container key={`package-reasons-${packageId}`} rowSpacing={1}>
					<Grid item xs={4}>
						<Typography variant='cardSubtitle2'>{`${
							values?.[0]?.packageName ?? ''
						}`}</Typography>
					</Grid>
					<Grid item xs={8}>
						<Typography variant='cardSubtitle2'>
							{values.map((reason) => reason.name).join(', ')}
						</Typography>
					</Grid>
				</Grid>
			))}
			{Object.entries(productReasons).map(([productId, values]) => (
				<Grid container key={`product-reasons-${productId}`} rowSpacing={1}>
					<Grid item xs={4}>
						<Typography variant='cardSubtitle2'>{`${
							values?.[0]?.productName ?? ''
						}`}</Typography>
					</Grid>
					<Grid item xs={8}>
						<Typography variant='cardSubtitle2'>
							{values.map((reason) => reason.name).join(', ')}
						</Typography>
					</Grid>
				</Grid>
			))}
		</Stack>
	);
};
/**
 * The `CreateQuoteSummaryPanel` component displays a summary of the quotation process.
 * It provides details like the base premium, client information, products, and limits.
 *
 * @param {Object} props - Component properties.
 * @param {string[]} props.declineQuestions - List of questions leading to quote decline.
//  * @param {boolean} props.showReferMessage - Flag to show referral message.
 * @param {React.ReactNode} props.children - Child components or elements to be rendered inside the card content.
 * @returns {JSX.Element} The rendered `CreateQuoteSummaryPanel` component.
 */
export const CreateQuoteSummaryPanel = (props: any) => {
	const {
		currentQuotes,
		needsReferral,
		isReferred,
		isDeclined,
		isQuotePolicyCreated,
		isReferralApproved,
        allReferralReasons,
		currentPremium,
		isQuoteBound,
		minPolicyPeriod,
		maxPolicyPeriod,
		maxForwardDays,
	} = useQuoteContext();

	const notBoundAndNeedsReferral = !isQuoteBound && needsReferral;

	const formik = props.options;

	return (
		<Card
			elevation={0}
			sx={{
				borderRadius: '7px',
				position: 'sticky',
				top: '1em',
				mb: '4em',
			}}
		>
			<Stack direction='row' p={'1em'} alignItems='center' justifyContent='space-between'>
				<Typography variant='cardContentHeader'>Quote Summary</Typography>
				<img src={CardTitleLogo} alt='Card Header Logo' style={{ height: '1.5em' }} />
			</Stack>
			<Divider light />
			<CardContent sx={{ p: 0 }}>
				<Stack p={'1em'}>
					<Typography variant='basePremiumHeader'>
						{`Your ${
							isReferred || isReferralApproved || notBoundAndNeedsReferral
								? 'Indicative '
								: ''
						}Base Premium`}
					</Typography>
					<Stack direction='row' justifyContent='left' alignItems='baseline'>
						<Typography
							variant='cardHeader'
							sx={{ fontSize: '3em', pl: 0, pr: '0.25em' }}
							display='inline'
						>
							{isReferred
								? 'Referred'
								: isDeclined
								? 'Declined'
								: formatCurrency(currentPremium)}
						</Typography>
						<Typography variant='subtitle2' display='inline'>
							<em>excl. charges</em>
						</Typography>
					</Stack>
				</Stack>
				<ThickDivider />
				<Box px={'1em'} pb={'0.5em'}>
					<Typography variant='cardSubtitle1'>Client information</Typography>
					<Grid container alignItems='center' spacing={1} py={'0.5em'}>
						<Grid item xs={'auto'}>
							<img src={buildingIconBlack} alt='Building Icon' />
						</Grid>
						<Grid item xs={true}>
							<Typography variant='cardSubtitle2'>
								{formik.values.companyName}
							</Typography>
						</Grid>
					</Grid>
					<Grid container alignItems='center' spacing={1} py={'0.5em'}>
						<Grid item xs={'auto'}>
							<img src={briefcaseIcon} alt='Briefcase Icon' />
						</Grid>
						<Grid item xs={true}>
							<Typography variant='cardSubtitle2'>
								{formik.values.occupation}
							</Typography>
						</Grid>
					</Grid>
				</Box>
				<ErrorMessage options={formik} />
				{/* {errors.isError && (
					<Box p="0.5em 1em" mb="1em" sx={{ backgroundColor: "rgba(220, 20, 60, 0.25)" }}>
						<Stack direction="row" alignItems={"center"}>
							<WarningOutlinedIcon
								color="error"
								sx={{ fontSize: "1.5em", mr: "0.25em" }}
							/>
							{typeof errors.message === "string" ? (
								<FormHelperText
									hidden={!errors.isError}
									error={errors.isError}
								>
									{errors.message}
								</FormHelperText>
							) : (
								(errors.message ?? []).length > 1 &&
								errors.isError && (
									<ul>
										{errors.message.map((message: any) => (
											<li key={`message-${message}`}>{message}</li>
										))}
									</ul>
								)
							)}
						</Stack>
					</Box>
				)} */}
				{props.addressComponent && (
					<>
						<ThickDivider />
						<Box px='1em' pb='1em'>
							<Stack direction='row'>
								<Typography mr='0.5em' variant='cardSubtitle1'>
									Policy period
								</Typography>
								<Tooltip
									title={`You can select a policy period between ${minPolicyPeriod} to ${maxPolicyPeriod} months. The start date cannot be in the past or more than ${maxForwardDays} days in the future.`}
									placement='right'
								>
									<InfoOutlinedIcon
										sx={{
											fontSize: '1em',
											color: colours.primaryDark.main,
										}}
									/>
								</Tooltip>
							</Stack>
							{props.addressComponent}
						</Box>
					</>
				)}
				{!isDeclined && props.declineQuestions.length > 0 && (
					<Stack px='1em' pb='1em'>
						<ThickDivider />
						<Typography variant='cardSubtitle1'>Decline</Typography>
						<Box
							width='100%'
							sx={{
								backgroundColor: '#F7E5B8',
							}}
						>
							<Typography variant='cardSubtitle1'>
								Your quote will be declined:
							</Typography>
							<List dense>
								{props.declineQuestions.map((q: any, i: number) => (
									<ListItem disablePadding key={`decline-questions-${i}-${q.id}`}>
										{/* {q.productName && <ListItemText primary={`${q.productName}`} />} */}
										{q.productName && (
											<Typography variant='cardSubtitle2'>
												{q.productName}
											</Typography>
										)}
										<Typography
											variant='cardSubtitle2'
											className='declineQuestions'
										>
											{Object.keys(declineMessages).includes(`${q.id}`)
												? declineMessages[q.id]
												: 'Quote will be declined based on answer to this question: '}
											{!Object.keys(declineMessages).includes(`${q.id}`) && (
												<QuestionText questionText={q.questionText} />
											)}
										</Typography>
									</ListItem>
								))}
							</List>
						</Box>
						<FormHelperText error>
							{
								'Quote cannot proceed with declined questions. Please remove declined product to continue.'
							}
						</FormHelperText>
					</Stack>
					// <div className="cardSummary_ReferInfo">
					// 	<ThickDivider />
					// 	<Typography variant="cardSubtitle1">Decline</Typography>

					// 	<div className="cardSummary_referInfoContainer">
					// 		<Typography variant="cardSubtitle1">
					// 			Your quote will be declined:
					// 		</Typography>
					// 		<ul>
					// 			{props.declineQuestions.map((q: any) => (
					// 				<li key={`decline-question-${q.id}`}>
					// 					{/* {Object.keys(declineMessages).includes(`${q.id}`) && declineMessages[q.id] : "Quote will be declined based on answer to this question: "} */}
					// 					{q.productName && (
					// 						<Typography variant="cardSubtitle2">
					// 							{q.productName}
					// 						</Typography>
					// 					)}
					// 					<Typography
					// 						variant="cardSubtitle2"
					// 						className="declineQuestions"
					// 					>
					// 						{Object.keys(declineMessages).includes(`${q.id}`)
					// 							? declineMessages[q.id]
					// 							: "Quote will be declined based on answer to this question: "}
					// 						{!Object.keys(declineMessages).includes(
					// 							`${q.id}`
					// 						) && <QuestionText questionText={q.questionText} />}
					// 					</Typography>
					// 				</li>
					// 			))}
					// 		</ul>
					// 	</div>
					// <div className="cardSummary_infoContainer createQuoteStepper_errorMessage">
					// 	<FormHelperText error>
					// 		{
					// 			"Quote cannot proceed with declined questions. Please remove declined product to continue."
					// 		}
					// 	</FormHelperText>
					// </div>
					// </div>
				)}
				{!isDeclined && (isReferred || isReferralApproved || notBoundAndNeedsReferral) && (
					<Stack pb='1em'>
						<ThickDivider />
						<Box px='1em' pb='0.5em'>
							<Typography variant='cardSubtitle1'>Refer</Typography>
						</Box>

						<Box
							width='100%'
							sx={{
								backgroundColor: isReferralApproved ? '#caf7b8' : '#F7E5B8',
							}}
						>
							<Box px='1em'>
								<Typography
									variant='subtitle1'
									sx={{
										fontSize: '14px',
									}}
								>
									{isReferralApproved
										? 'Referral approved'
										: isReferred
										? 'Referral sent. Please note the Base Premium is indicative and may change when referred to a Delta Underwriter.'
										: 'Referral needed. Please note the Base Premium is indicative and may change when referred to a Delta Underwriter.'}
								</Typography>
								{!isReferralApproved && !isReferred && allReferralReasons.length > 0 && <ReferralMessage />}
							</Box>
						</Box>
					</Stack>
				)}
				{props.children}
			</CardContent>
		</Card>
	);
};
